import type {CopilotAssignmentResponse} from './copilot-assignment-api'

/**
 * Enhances error messages based on HTTP status codes with actionable guidance.
 *
 * @param baseMessage - The base error message
 * @param status - The HTTP status code
 * @returns An enhanced error message with user guidance
 */
export function enhanceErrorMessage(baseMessage: string, status: number): string {
  switch (status) {
    case 403:
      return `${baseMessage}: Please check that you have permission to assign issues in the target repository.`

    case 404:
      return `${baseMessage}: The repository or branch may not exist or may not be accessible.`

    case 422:
      return `${baseMessage}: Please check that the selected repository and branch are valid.`

    case 429:
      return `${baseMessage}: Rate limit exceeded. Please wait a few moments and try again.`

    case 0: // Network error or exception
      return `${baseMessage}. Please check your network connection and try again.`

    default:
      if (status >= 500) {
        return `${baseMessage}: There was a server error. Please try again in a few moments.`
      }
      return `${baseMessage}. Please try again or contact support if the issue persists.`
  }
}

/**
 * Formats error messages for Copilot assignment API responses.
 *
 * @param response - The API response object containing error information
 * @param baseMessage - The base error message to enhance
 * @returns A formatted error message string
 */
export function formatAssignmentError(response: CopilotAssignmentResponse, baseMessage: string): string {
  return enhanceErrorMessage(baseMessage, response.status)
}

/**
 * Formats error messages for caught exceptions.
 *
 * @param error - The caught error
 * @param operation - Brief description of the operation that failed
 * @returns A formatted error message string
 */
export function formatException(error: unknown, operation: string): string {
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
  return enhanceErrorMessage(`${operation} failed - ${errorMessage}`, 0)
}
